import { GridRowId } from '@mui/x-data-grid-premium';
import styles from './ContractsTree.module.scss';
import {
    Button,
    ButtonGroup,
    LoadingDataGrid,
} from '@thought-river/ui-components';
import { Add, Gesture, Replay } from '@mui/icons-material';
import ContractUploadWizardModal from 'src/components/ContractUploadWizardModal/ContractUploadWizardModal-container';
import { useState } from 'react';
import { ContractsDataGrid } from './components';
import {
    ContractTree,
    ContractTreeRow,
    useGetContractsV10,
} from '@thought-river/negotiations-common/dist/api/contractManagement';
import ContractResource from 'src/resources/ContractResource';
import { updateTanStackQueryContractsRow } from './helpers';
import { useQueryClient } from '@tanstack/react-query';
import { usePostSignContract } from '@thought-river/negotiations-common/dist/api/contractContent';

const ContractsTree = () => {
    const queryClient = useQueryClient();

    const [contractUploadModalOpen, setContractUploadModalOpen] =
        useState(false);

    const { mutateAsync: signContract } = usePostSignContract({});

    const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);

    const { data: contractsData, isLoading: contractsLoading } =
        useGetContractsV10(
            {},
            {
                refetchOnWindowFocus: false,
            }
        );

    const contracts = contractsData as ContractTree;

    //Potentially won't need it once long polling contract status endpoints are done
    const handleContractUpdated = (
        uuid: string,
        status: string,
        processingStatus: string
    ) => {
        const newContractRow: ContractTreeRow = contracts?.rows.find(
            (r) => r.contract_uuid === uuid
        )!;

        updateTanStackQueryContractsRow(queryClient, {
            ...newContractRow,
            version_status: status,
            processing_status: processingStatus,
        });

        setSelectedRows([]);
    };

    const reanalyzeContracts = () => {
        const contractRows = contracts?.rows.filter((row) =>
            selectedRows.includes(row.id)
        );
        contractRows?.forEach(
            ({ contract_uuid, deal_type, version_status }) => {
                ContractResource.reprocess([contract_uuid], deal_type).then(
                    () => {
                        handleContractUpdated(
                            contract_uuid,
                            version_status,
                            'processing'
                        );
                    }
                );
            }
        );
    };

    const signContracts = () => {
        const contractRows = contracts?.rows.filter((row) =>
            selectedRows.includes(row.id)
        );

        contractRows?.forEach(
            ({ contract_uuid, version, processing_status }) => {
                signContract(
                    {
                        pathParams: {
                            contractUuid: contract_uuid,
                            contractVersion: version.number,
                        },
                    },
                    {
                        onSuccess: () => {
                            handleContractUpdated(
                                contract_uuid,
                                'signed',
                                processing_status
                            );
                        },
                    }
                );
            }
        );
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.actionsBar}>
                <Button
                    onClick={() => setContractUploadModalOpen(true)}
                    startIcon={<Add />}
                    data-testid="new-negotiation-btn"
                >
                    New negotiation
                </Button>
                {selectedRows.length ? (
                    <>
                        <ButtonGroup>
                            <Button
                                startIcon={<Gesture />}
                                variant="secondary"
                                onClick={signContracts}
                            >
                                Sign {selectedRows.length} contracts
                            </Button>
                            <Button
                                startIcon={<Replay />}
                                variant="secondary"
                                onClick={reanalyzeContracts}
                            >
                                Reanalyze {selectedRows.length} contacts
                            </Button>
                        </ButtonGroup>
                    </>
                ) : (
                    ''
                )}
            </div>
            {contractsLoading ? (
                <LoadingDataGrid />
            ) : (
                <ContractsDataGrid
                    data={{
                        rows: contracts?.rows || [],
                        columns: contracts?.columns || [],
                    }}
                    rowSelectionModel={selectedRows}
                    onSelectedRowsChange={setSelectedRows}
                    onContractUpdated={handleContractUpdated}
                />
            )}
            <ContractUploadWizardModal
                open={contractUploadModalOpen}
                onClose={() => setContractUploadModalOpen(false)}
            />
        </div>
    );
};

export default ContractsTree;
